import {
  convertDateToYYYYMMDDFormat,
  getFirstDayOfMonth,
  getToday,
} from '@repo/ui';
import { useFormik } from 'formik';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { useProfessionalDailyWorkloadReport } from './useProfessionalDailyWorkloadReport';

const DEFAULT_ROWS_PER_PAGE = 10;

type DailyWorkloadFilterValues = {
  startDate: string;
  endDate: string;
  periodType: 'ACTIVITY_DATE' | 'VALIDATION_DATE';
  semester: { label: string; value: string } | null;
  unit: { label: string; value: string } | null;
  group: { label: string; value: string } | null;
  professional: { label: string; value: string } | null;
  search: string;
};

const buildFilterParams = (values: DailyWorkloadFilterValues) => ({
  periodType: values.periodType,
  startDate: values.startDate,
  endDate: values.endDate,
  semesterHashId: values.semester?.value || undefined,
  curricularUnitHashId: values.unit?.value || undefined,
  groupHashId: values.group?.value || undefined,
  professionalHashId: values.professional?.value || undefined,
  search: values.search || undefined,
});

export const useDailyWorkloadReport = () => {
  const {
    statusGet,
    statusExport,
    semesters,
    curricularUnits,
    groups,
    professionals,
    screenData,
    requestGetAllSemestersUser,
    requestGetAllCurricularUnitsUser,
    requestGetAllGroupsUser,
    requestGetAllProfessionalsSelectOptions,
    requestDailyWorkloadScreen,
    exportDailyWorkloadExcel,
  } = useProfessionalDailyWorkloadReport();

  const [selectedPage, setSelectedPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const makeRequestParams = (
    values: DailyWorkloadFilterValues,
    page: number,
    length: number = rowsPerPage,
  ) => ({
    ...buildFilterParams(values),
    page,
    length,
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      startDate: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
      endDate: convertDateToYYYYMMDDFormat(getToday()),
      periodType: 'ACTIVITY_DATE' as 'ACTIVITY_DATE' | 'VALIDATION_DATE',
      semester: null as { label: string; value: string } | null,
      unit: null as { label: string; value: string } | null,
      group: null as { label: string; value: string } | null,
      professional: null as { label: string; value: string } | null,
      search: '',
    },
    validationSchema: Yup.object({
      periodType: Yup.string()
        .oneOf(['ACTIVITY_DATE', 'VALIDATION_DATE'])
        .required('Tipo de período é obrigatório.'),
      startDate: Yup.date().required('Data inicial é obrigatória.'),
      endDate: Yup.date().required('Data final é obrigatória.'),
      semester: Yup.object().nullable(),
      unit: Yup.object().nullable(),
      group: Yup.object().nullable(),
      professional: Yup.object().nullable(),
      search: Yup.string(),
    }),
    onSubmit: (values) => {
      setSelectedPage(1);
      requestDailyWorkloadScreen(makeRequestParams(values, 1));
    },
  });

  const dataSource = screenData;
  const hasDataSource = (dataSource?.professionals?.length || 0) > 0;
  const totalItems = dataSource?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

  const pages = useMemo(
    () => Array.from({ length: totalPages }, (_, index) => index + 1),
    [totalPages],
  );

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;

    setSelectedPage(page);
    requestDailyWorkloadScreen(
      makeRequestParams(validation.values, page, rowsPerPage),
    );
  };

  const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number(event.target.value);

    setRowsPerPage(newRowsPerPage);
    setSelectedPage(1);

    requestDailyWorkloadScreen(
      makeRequestParams(validation.values, 1, newRowsPerPage),
    );
  };

  const handleExportExcel = () => {
    exportDailyWorkloadExcel(buildFilterParams(validation.values));
  };

  useEffect(() => {
    requestGetAllSemestersUser();
    requestDailyWorkloadScreen(makeRequestParams(validation.values, 1));
  }, []);

  useEffect(() => {
    requestGetAllCurricularUnitsUser({
      entityId: undefined,
      courseId: undefined,
    });

    requestGetAllGroupsUser({
      entityId: undefined,
      courseId: undefined,
      semesterId: validation.values.semester?.value,
      curricularUnitId: validation.values.unit?.value || undefined,
    });

    requestGetAllProfessionalsSelectOptions({
      semester: validation.values.semester?.value,
      curricularUnit: validation.values.unit?.value || undefined,
      groups: validation.values.group?.value
        ? [validation.values.group.value]
        : [],
    });
  }, [
    validation.values.semester?.value,
    validation.values.unit?.value,
    validation.values.group?.value,
  ]);

  return {
    validation,
    statusGet,
    statusExport,
    semesters,
    curricularUnits,
    groups,
    professionals,
    handleExportExcel,
    dataSource,
    hasDataSource,
    totalItems,
    selectedPage,
    totalPages,
    pages,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
  };
};
